import * as React from 'react';
import { BreadcrumbItem } from '@ixicare/ui';

/**
 * Two slots rather than one array: a route-scope wrapper (siteWrapper) owns the leading crumbs
 * while the page owns the tail. Both register on mount and layout effects run child-first, so a
 * single slot would let the wrapper's shorter trail clobber the page's.
 */
interface BreadcrumbsContextValue {
  setPrefix: (items: BreadcrumbItem[]) => void;
  setPage: (items: BreadcrumbItem[]) => void;
}

const noop = () => {};

const BreadcrumbsContext = React.createContext<BreadcrumbsContextValue>({ setPrefix: noop, setPage: noop });

export const BreadcrumbsProvider = BreadcrumbsContext.Provider;

const useCrumbSlot = (set: (items: BreadcrumbItem[]) => void, items: BreadcrumbItem[]) => {
  // Callers pass a fresh array each render; key the effect on the crumb contents instead.
  const key = items.map((i) => `${i.label}|${i.href ?? ''}`).join('>');
  React.useLayoutEffect(() => {
    set(items);
    return () => set([]);
  }, [key, set]);
};

/**
 * Register the page's crumbs with the layout that renders them. The trail can change without a
 * route change (the in-map floor selector), so it is pushed up from the page rather than derived
 * from the URL. The trailing crumb is the page itself and should not link.
 */
export const useBreadcrumbs = (items: BreadcrumbItem[]) => {
  const { setPage } = React.useContext(BreadcrumbsContext);
  useCrumbSlot(setPage, items);
};

/** Register the crumbs a route-scope wrapper contributes ahead of every page beneath it. */
export const useBreadcrumbPrefix = (items: BreadcrumbItem[]) => {
  const { setPrefix } = React.useContext(BreadcrumbsContext);
  useCrumbSlot(setPrefix, items);
};
